import type { CoverResponse, CreditInsurerRequestResponse } from "@asap/shared/schemas/credit-insurance";
import type { DayOfWeek } from "@asap/shared/utils/supabase.enum";

// Rough cost of one profile on an order, used for the indicative comparison only.
const ESTIMATED_AMOUNT_PER_PROFILE_EUR = 7_000;

// An ND request carries no explicit amount: it covers up to the ND threshold.
export const ND_REQUESTED_AMOUNT_EUR = 20_000;

export type CoverageNoticeState =
  | { kind: "active"; coveredAmount: number }
  | { kind: "pending"; requestedAmount: number | null }
  | { kind: "refused" }
  | { kind: "failed" }
  | { kind: "none" };

// The cover's total, or the sum of its parts when the insurer gave no total yet.
const getCoverAmount = (cover: Pick<CoverResponse, "totalAmount" | "named" | "nd">): number | null => {
  if (cover.totalAmount !== null) return cover.totalAmount;
  const partAmounts = [cover.named?.amount, cover.nd?.amount].filter((amount) => typeof amount === "number");
  return partAmounts.length > 0 ? partAmounts.reduce((sum, amount) => sum + amount, 0) : null;
};

/**
 * What the OPS should see about a company's cover. An approved cover wins over everything else;
 * otherwise an ongoing request, then a refusal, then a technical failure, and only when nothing
 * was ever requested do we fall back to "none" (the only state that proposes a new request).
 */
export const getCoverageNoticeState = (
  covers: Pick<CoverResponse, "status" | "totalAmount" | "named" | "nd">[],
  requests: Pick<CreditInsurerRequestResponse, "status" | "type" | "requestedAmount" | "createdAt">[]
): CoverageNoticeState => {
  const coveredAmount = covers
    .filter((cover) => cover.status === "approved")
    .reduce((sum, cover) => sum + (cover.totalAmount ?? 0), 0);
  if (coveredAmount > 0) return { kind: "active", coveredAmount };

  const pendingRequest = requests.find((request) => request.status === "pending");
  if (pendingRequest) {
    const requestedAmount =
      pendingRequest.type === "nd_cover" ? ND_REQUESTED_AMOUNT_EUR : pendingRequest.requestedAmount;
    return { kind: "pending", requestedAmount };
  }
  const pendingCover = covers.find((cover) => cover.status === "pending");
  if (pendingCover) return { kind: "pending", requestedAmount: getCoverAmount(pendingCover) };
  if (covers.some((cover) => cover.status === "refused")) return { kind: "refused" };

  const latestRequest = [...requests].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
  if (latestRequest?.status === "failed") return { kind: "failed" };

  return { kind: "none" };
};

/** Indicative order amount; `null` when the number of profiles is not a usable positive integer. */
export const estimateOrderAmount = (numberOfProfiles: number | undefined): number | null => {
  if (typeof numberOfProfiles !== "number" || !Number.isInteger(numberOfProfiles) || numberOfProfiles <= 0) {
    return null;
  }
  return numberOfProfiles * ESTIMATED_AMOUNT_PER_PROFILE_EUR;
};

const DAY_OF_WEEK_BY_UTC_INDEX: DayOfWeek[] = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

const countWorkedDays = (startDate: string, endDate: string, workingDays: DayOfWeek[]): number => {
  const start = Date.parse(startDate);
  const end = Date.parse(endDate);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return 0;

  let workedDays = 0;
  for (let time = start; time <= end; time += MILLISECONDS_PER_DAY) {
    if (workingDays.includes(DAY_OF_WEEK_BY_UTC_INDEX[new Date(time).getUTCDay()])) workedDays += 1;
  }
  return workedDays;
};

type ContractEstimateInput = {
  startDate: string | undefined;
  endDate: string | undefined;
  workingDays: DayOfWeek[] | undefined;
  weeklyHours: number | undefined;
  hourlyRate: number | undefined;
  coefficient: number | undefined;
};

const isPositiveNumber = (value: number | undefined): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

/**
 * Indicative contract amount: worked days × hours per day × hourly rate × coefficient.
 * `null` as long as one of the inputs is missing or unusable.
 */
export const estimateContractAmount = (input: ContractEstimateInput): number | null => {
  const { startDate, endDate, workingDays, weeklyHours, hourlyRate, coefficient } = input;
  if (!startDate || !endDate || !workingDays || workingDays.length === 0) return null;
  if (!isPositiveNumber(weeklyHours) || !isPositiveNumber(hourlyRate) || !isPositiveNumber(coefficient)) return null;

  const workedDays = countWorkedDays(startDate, endDate, workingDays);
  if (workedDays === 0) return null;

  const hoursPerDay = weeklyHours / workingDays.length;
  return workedDays * hoursPerDay * hourlyRate * coefficient;
};

/** What a creation form knew about the cover while the OPS filled it in. */
export type CoverageSnapshot = { state: CoverageNoticeState; estimatedAmount: number | null };

/** Whether the estimate is above the cover; `null` when either side is unknown. */
export const isEstimateAboveCover = ({ state, estimatedAmount }: CoverageSnapshot): boolean | null => {
  if (estimatedAmount === null) return null;
  return estimatedAmount > (state.kind === "active" ? state.coveredAmount : 0);
};

/**
 * The shortfall of a snapshot, or `null` when creating is covered (or when the amount to cover is
 * unknown for an active cover). Without an active cover, the whole estimate is uncovered.
 */
export const getCoverageShortfall = (snapshot: CoverageSnapshot): { gapAmount: number | null } | null => {
  const { state, estimatedAmount } = snapshot;
  if (state.kind !== "active") return { gapAmount: estimatedAmount };
  if (estimatedAmount === null || estimatedAmount <= state.coveredAmount) return null;
  return { gapAmount: estimatedAmount - state.coveredAmount };
};
